import {goalScope} from '../my-objective/my-goals-v2.utils';
import {teamMemberName} from '../direct-reports/team-goals.utils';
import {
  AllGoalsGroupBy,
  AllGoalSection,
  AllGoalSectionIdentity,
} from './all-goals.types';

export const goalEntityId = (value: any): string =>
  typeof value === 'string' ? value : value?.id || value?._id || '';

const resolveUser = (value: any, users: any[]) => {
  if (value && typeof value === 'object') {
    const stored = users.find(
      (user) => goalEntityId(user) === goalEntityId(value),
    );
    return stored
      ? {
          ...stored,
          ...value,
          groups: value.groups || stored.groups,
          group: value.group || stored.group,
        }
      : value;
  }
  return users.find((user) => goalEntityId(user) === value);
};

export const allGoalOwner = (goal: any, users: any[]) =>
  resolveUser(goal?.assignee, users);

export const flattenAllGoals = (members: any[]): any[] =>
  Array.from(
    members
      .flatMap((member) =>
        (member?.goals || []).map((goal: any) => ({
          ...goal,
          assignee: goal?.assignee || member?.assignee,
        })),
      )
      .filter((goal) => goalEntityId(goal))
      .reduce((goals, goal) => {
        const id = goalEntityId(goal);
        if (!goals.has(id)) goals.set(id, goal);
        return goals;
      }, new Map<string, any>())
      .values(),
  );

const resolveGroup = (value: any, groups: any[]) => {
  const groupValue = value?.group || value?.groupId || value;
  if (groupValue && typeof groupValue === 'object') return groupValue;
  return groups.find((item) => goalEntityId(item) === groupValue);
};

const goalGroup = (goal: any, groups: any[]) => {
  const values: any[] = Array.isArray(goal?.group) ? goal.group : [goal?.group];
  return values.map((value) => resolveGroup(value, groups)).find(Boolean);
};

const ownerGroup = (owner: any, groups: any[]) => {
  const memberships = [
    ...(Array.isArray(owner?.groups) ? owner.groups : []),
    ...(Array.isArray(owner?.group) ? owner.group : [owner?.group]),
  ].filter(Boolean);
  return memberships
    .map((membership) => resolveGroup(membership, groups))
    .find(Boolean);
};

export const allGoalGroup = (
  goal: any,
  users: any[],
  groups: any[] = [],
  emptyLabel = 'No group',
): AllGoalSectionIdentity => {
  if (goalScope(goal) === 'company') {
    return {id: 'company', name: 'Company', kind: 'company'};
  }

  const owner = allGoalOwner(goal, users);
  const group = goalGroup(goal, groups) || ownerGroup(owner, groups);
  if (!group) {
    return {id: 'unassigned', name: emptyLabel, kind: 'unassigned'};
  }
  return {
    id: `group:${goalEntityId(group)}`,
    name: group.name || goal?.groupName || 'Group',
    kind: 'group',
  };
};

export const allGoalTeam = (
  goal: any,
  users: any[],
): AllGoalSectionIdentity => {
  const owner = allGoalOwner(goal, users);
  const reviewer = resolveUser(owner?.reviewer, users);
  if (reviewer) {
    const managerName = reviewer?.firstName || teamMemberName(reviewer);
    return {
      id: `team:${goalEntityId(reviewer)}`,
      name: `${managerName}’s Team`,
      kind: 'team',
    };
  }

  const ownerId = goalEntityId(owner);
  const ownerLeadsTeam =
    ownerId &&
    users.some(
      (user) => goalEntityId(resolveUser(user?.reviewer, users)) === ownerId,
    );
  if (ownerLeadsTeam) {
    const managerName = owner?.firstName || teamMemberName(owner);
    return {
      id: `team:${ownerId}`,
      name: `${managerName}’s Team`,
      kind: 'team',
    };
  }

  return {id: 'unassigned', name: 'Unassigned', kind: 'unassigned'};
};

export const buildAllGoalSections = (
  goals: any[],
  users: any[],
  groups: any[] = [],
  groupBy: Exclude<AllGoalsGroupBy, 'none'> = 'group',
  emptyGroupLabel = 'No group',
): AllGoalSection[] => {
  const sections = new Map<string, AllGoalSection>();

  goals.forEach((goal) => {
    const identity =
      groupBy === 'group'
        ? allGoalGroup(goal, users, groups, emptyGroupLabel)
        : allGoalTeam(goal, users);
    const section = sections.get(identity.id) || {
      ...identity,
      goals: [],
      ownerCount: 0,
    };
    section.goals.push(goal);
    sections.set(identity.id, section);
  });

  return Array.from(sections.values())
    .map((section) => ({
      ...section,
      ownerCount: new Set(
        section.goals
          .map((goal) => goalEntityId(goal?.assignee))
          .filter(Boolean),
      ).size,
    }))
    .sort((first, second) => {
      const rank = {company: 0, group: 1, team: 1, unassigned: 2};
      const rankDifference = rank[first.kind] - rank[second.kind];
      return rankDifference || first.name.localeCompare(second.name);
    });
};
